import React, { useRef } from 'react';
import { COLORS, TYPE, WEIGHT, LETTER, SPACE } from '../styles/tokens';
import GlassCard from './ui/GlassCard';
import IconBadge from './ui/IconBadge';
import SectionHeading from './ui/SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';
import { useT } from '../i18n/I18nContext';
import { useTrackSection } from '../hooks/useTrackSection';

const ICONS: React.ReactNode[] = [
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"
          stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="3" y1="6" x2="21" y2="6" stroke="white" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M16 10a4 4 0 01-8 0" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.7" />
    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="white" strokeWidth="1.7" />
    <path d="M8 7h8M8 11h8M8 15h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
];
const TINTS = [COLORS.tintPink, COLORS.tintPurple, COLORS.tintBlue, COLORS.tintMint];
const GLOWS = [COLORS.glowPink, COLORS.glowPurple, COLORS.glowBlue, COLORS.glowMint];

export default function WhyBetterSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const headRef  = useScrollReveal<HTMLDivElement>({ fromY: 26, duration: 0.7, start: 'top 80%' });
  const cardsRef = useScrollReveal<HTMLDivElement>({
    fromY: 22, fromScale: 0.97, duration: 0.6, stagger: 0.1, start: 'top 82%',
    selector: '.why-card',
  });
  useTrackSection('why_better', sectionRef);

  return (
    <section
      ref={sectionRef}
      id="why-better"
      style={{
        position: 'relative',
        padding: `${SPACE.sectionY} ${SPACE.sectionX}`,
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div ref={headRef} style={{ marginBottom: 'clamp(32px, 4vw, 52px)', opacity: 0 }}>
          <SectionHeading title={t.whyBetter.titleA} secondLine={t.whyBetter.titleB} />
        </div>

        <div
          ref={cardsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: SPACE.gridGap,
          }}
          className="why-grid"
        >
          {t.whyBetter.cards.map((c, i) => (
            <GlassCard key={i} hoverGlow={GLOWS[i]} className="why-card" padding="clamp(20px, 2.5vw, 26px)">
              <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
                <IconBadge tint={TINTS[i]} size={48} radius={15}>{ICONS[i]}</IconBadge>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h3 style={{
                    fontSize: TYPE.cardTitle, fontWeight: WEIGHT.semibold,
                    color: COLORS.textPrimary,
                    marginBottom: 7, lineHeight: 1.3,
                    letterSpacing: LETTER.tight,
                  }}>
                    {c.title}
                  </h3>
                  <p style={{
                    fontSize: TYPE.cardText, color: COLORS.textBody,
                    lineHeight: 1.65, fontWeight: WEIGHT.regular,
                  }}>
                    {c.text}
                  </p>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: 'clamp(28px, 3.5vw, 40px)',
          padding: 'clamp(16px, 2vw, 22px) clamp(20px, 3vw, 32px)',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 16,
          maxWidth: 640,
          margin: 'clamp(28px, 3.5vw, 40px) auto 0',
        }}>
          <p style={{
            fontSize: TYPE.sectionSub,
            color: 'rgba(255,255,255,0.65)',
            lineHeight: 1.65,
            fontStyle: 'italic',
          }}>
            {t.whyBetter.tagline}
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .why-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
